// @ts-nocheck
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import siteMessage from "@insite/client-framework/SiteMessage";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import FormWrapper from "@store/Library/Components/Toolkit/FormWrapper";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import { formGridProps } from "@store/Themes/common/props/form";
import { submitAccountSignUp } from "@store/Framework/Services/AccountSignUpService";
import { contactUsAvailableTopics } from "@store/Overrides/Widgets/ContactUs/AvailableTopics";
import React, { ReactNode, useState } from "react";
import { css } from "styled-components";

const enum fields {
    contactUsDestination = "contactUsDestination",
    signInDestination = "signInDestination",
}

export interface SignUpStyles {
    form?: InjectableCss;
    gridContainer?: GridContainerProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldProps;
    actionsGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
}

export const signUpStyles: SignUpStyles = {
    gridContainer: formGridProps.gridContainer,
    emailGridItem: {
        width: 12,
    },
    actionsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: flex-end;
        `,
    },
    submitButton: {
        variant: "tertiary",
        icon: {
            src: ArrowForward,
            position: "right",
        },
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.contactUsDestination]: LinkFieldValue;
        [fields.signInDestination]: LinkFieldValue;
    };
}

const styles = signUpStyles;
const StyledForm = getStyledWrapper("form");
type SignUpProps = OwnProps;

const SignUp: React.FC<SignUpProps> = ({ fields }) => {
    const [emailAddress, setEmailAddress] = useState("");
    const [didAttemptSubmit, setDidAttemptSubmit] = useState(false);
    const [emailErrorMessage, setEmailErrorMessage] = useState<ReactNode>("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const contactUsUrl = useGetLink(fields.contactUsDestination)?.url;
    const signInUrl = useGetLink(fields.signInDestination)?.url;

    const validateEmailAddress = (value: string): ReactNode => {
        const trimmedValue = value.trim();

        if (!trimmedValue) {
            return siteMessage("SignInInfo_Email_Required");
        }

        if (!validateEmail(trimmedValue)) {
            return siteMessage("SignInInfo_Email_Validation");
        }

        return "";
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setDidAttemptSubmit(true);

        const sanitizedEmail = emailAddress.trim();
        const validationMessage = validateEmailAddress(sanitizedEmail);
        if (validationMessage) {
            setEmailErrorMessage(validationMessage);
            return;
        }

        setEmailErrorMessage("");
        setEmailAddress(sanitizedEmail);
        setIsSubmitting(true);

        try {
            const response = await submitAccountSignUp(sanitizedEmail);

            if (!response.successful) {
                setEmailErrorMessage(translate("Please enter a valid email address"));
                return;
            }

            if (response.result.status === 204) {
                if (contactUsUrl) {
                    window.location.href = `${contactUsUrl}?topic=${contactUsAvailableTopics.createAccount.value}`;
                    return;
                }
            }

            if (response.result.status === 200) {
                const emailStatus = response.result.emailStatus;
                if (emailStatus === "activationSent" || emailStatus === "passwordSent") {
                    if (signInUrl) {
                        window.location.href = `${signInUrl}?emailSent=y`;
                    }

                    return;
                }
            }

            if (response.result.status < 200 || response.result.status >= 300) {
                setEmailErrorMessage(translate("An unexpected error occurred."));
            }
        } catch (error) {
            setEmailErrorMessage(translate("An unexpected error occurred."));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        setEmailAddress(value);

        if (didAttemptSubmit) {
            setEmailErrorMessage(validateEmailAddress(value));
        }
    };

    return (
        <FormWrapper>
            <StyledForm {...styles.form} onSubmit={handleSubmit} noValidate>
                <GridContainer {...styles.gridContainer}>
                    <GridItem {...styles.emailGridItem}>
                        <TextField
                            id="signUp_email"
                            {...styles.emailTextField}
                            label={translate("Enter your email address to get started")}
                            value={emailAddress}
                            error={emailErrorMessage}
                            onChange={handleEmailChange}
                            autoComplete="email"
                            data-test-selector="signUp_email"
                            required
                            type="email"
                        />
                    </GridItem>
                    <GridItem {...styles.actionsGridItem}>
                        <FormActions>
                            <Button
                                {...styles.submitButton}
                                type="submit"
                                data-test-selector="signUp_next"
                                disabled={isSubmitting}
                            >
                                {translate("Next")}
                            </Button>
                        </FormActions>
                    </GridItem>
                </GridContainer>
            </StyledForm>
        </FormWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: SignUp,
    definition: {
        group: "Sign In",
        icon: "LogIn",
        allowedContexts: ["SignInPage", "SignInOverlay"],
        fieldDefinitions: [
            {
                name: fields.contactUsDestination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
                tab: basicTab,
            },
            {
                name: fields.signInDestination,
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Contextual",
                isRequired: true,
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
